import React, {ChangeEvent, useMemo} from 'react';

import {Avatar} from 'src/components/avatar/avatar';
import {useUserEmail} from 'src/hooks/useUserEmail';
import {useUserRoles} from 'src/hooks/useUserRoles';
import {ChangeRequest} from 'src/openapi/models/ChangeRequest';
import {ChangeRequestStatus} from 'src/openapi/models/ChangeRequestStatus';
import {Role} from 'src/openapi/models/Role';
import {User} from 'src/openapi/models/User';

import {Autosuggest, AutosuggestItem, AutosuggestNoResults} from '@pb/kit-component-autosuggest-v2';
import {Button} from '@pb/kit-component-button';
import {Dialog, DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Dropdown} from '@pb/kit-component-dropdown-v2';
import {Group, Item} from '@pb/kit-component-group';
import {Icons, Sizes} from '@pb/kit-component-icon';
import {BasicIconButton, InfoIconButton} from '@pb/kit-component-icon-button';
import {Tooltip} from '@pb/kit-component-popups';

import {authorWarningMessage} from './change-author.module.styl';

type Props = {
    shown: boolean,
    value: string,
    selectedUser: User | null,
    suggestUsers: Array<User>,
    changeRequest: ChangeRequest,
    onChangeQuery: (event: ChangeEvent<HTMLInputElement>) => void,
    onItemSelect: (user: User) => void,
    onClear: () => void,
    onApply: () => void,
    onToggle: () => void,
    onClose: () => void,
};

const INFO = 'Assign this Change Request to another user. The new author will get editing rights after confirmation.';

export const ChangeAuthorView = (props: Props) => {
    const {
        shown,
        value,
        selectedUser,
        suggestUsers,
        changeRequest,
        onChangeQuery,
        onItemSelect,
        onClear,
        onApply,
        onToggle,
        onClose,
    } = props;

    const userEmail = useUserEmail();
    const userRoles = useUserRoles();

    const hasFinalStatus = changeRequest.status === ChangeRequestStatus.APPROVED ||
        changeRequest.status === ChangeRequestStatus.REJECTED;
    const hasRequiredRole = userRoles.includes(Role.ROLE_UDEDT_APPROVER)
        || userRoles.includes(Role.ROLE_UDEDT_REVIEWER);
    const isUserAuthor = changeRequest?.authorEmail === userEmail;
    const hasUserAccess = isUserAuthor || hasRequiredRole;
    const canBeUpdated = !hasFinalStatus && hasUserAccess;

    const users = useMemo(() => suggestUsers.map(user => (
        <AutosuggestItem
            key={user.email}
            value={user}
            caption={(
                <Group alignItems="center">
                    <Avatar user={user}/>
                    <Item>{user.firstName} {user.lastName}</Item>
                </Group>
            )}
            disabled={false}
        />
    )), [suggestUsers]);

    return (
        <>
            {canBeUpdated ? (
                <Dialog
                    shown={shown}
                    anchor={({anchorRef}) => (
                        <Tooltip
                            anchor={() => (
                                <BasicIconButton
                                    ref={anchorRef}
                                    iconProps={{type: Icons.EDIT, size: Sizes.Size_16}}
                                    onClick={onToggle}
                                />
                            )}>
                            Change Author
                        </Tooltip>
                    )}>
                    <DialogHeader onClose={onClose}>Change Author</DialogHeader>
                    <DialogMain>
                        <Group className="pdx-m pdy-m">
                            <Item style={{width: '100%'}}>
                                <Group direction="v" gap="none" className={`${authorWarningMessage} pdy-m pdx-m`}>
                                    <Item>Changing the author <strong>is irreversible</strong>.</Item>
                                    <Item>
                                        Once applied, <strong>you will lose edit access to this Change Request</strong>.
                                    </Item>
                                    <Item>Only the new author will be able to make changes.</Item>
                                </Group>
                                <Group gap="xs" className="pb-m pt-l">
                                    <Item>New Author</Item>
                                    <Dropdown
                                        hoverEnabled={true}
                                        clickEnabled={false}
                                        maxWidth={500}
                                        anchor={({anchorRef, getAnchorProps}) => (
                                            <InfoIconButton {...getAnchorProps()} ref={anchorRef}/>
                                        )}>
                                        {INFO}
                                    </Dropdown>
                                </Group>
                                <Autosuggest
                                    placeholder="Search for user"
                                    value={value}
                                    onChange={onChangeQuery}
                                    onItemSelect={onItemSelect}
                                    onClear={onClear}>
                                    {users.length ? users : <AutosuggestNoResults/>}
                                </Autosuggest>
                            </Item>
                        </Group>
                    </DialogMain>
                    <DialogFooter>
                        <Group justify="end" style={{width: '100%'}}>
                            <Button
                                onClick={onApply}
                                disabled={!selectedUser?.email}
                                variant="Type.PRIMARY">
                                Apply
                            </Button>
                        </Group>
                    </DialogFooter>
                </Dialog>
            ) : null}
        </>
    );
};
